import { alpha, email, maxLength, minLength, numeric, required } from 'vuelidate/lib/validators'
import moment from 'moment'
import * as vuelidate from 'vuelidate'

function isUserInternal(userType) {
  return userType === 'Internal'
}

function getEventDuration(formData) {
  return Number(formData.endTime.replace(':', '')) - Number(formData.startTime.replace(':', ''))
}

function getDeliverPeriod(formData) {
  if (formData.deliverBy && formData.eventDate) {
    const a = moment(formData.deliverBy)
    const b = moment(formData.eventDate)
    const diff = a.diff(b, 'days')
    return diff
  } else {
    return 0
  }
}

/* Custom Validation Rules
---------------------------------------------------------------- */
export const min = moment(new Date()).add(30, 'd').format('YYYY-MM-DD')
export const minDate = vuelidate.withParams({ minDate: min }, value => moment(value, 'YYYY-MM-DD', true).isSameOrAfter(min))
export const weekday = vuelidate.withParams({ minDate: min }, value => {
  const d = new Date(value).getDay()
  return d === 0 || d === 1 || d === 2 || d === 3 || d === 4
})
export const minTime = (min) => {
  return vuelidate.withParams({ type: 'minTime', min }, (value) => {
    const time = Number(value.replace(':', ''))
    return value ? time >= min : false
  })
}
export const maxTime = (max) => {
  return vuelidate.withParams({ type: 'maxTime', max }, (value) => {
    const time = Number(value.replace(':', ''))
    return value ? !(time > max) : false
  })
}
export const minEventDuration = (duration) => {
  return vuelidate.withParams({ type: 'minEventDuration', duration }, () => {
    return duration >= 100
  })
}
export const maxEventDuration = (duration) => {
  return vuelidate.withParams({ type: 'maxEventDuration', duration }, () => {
    return duration <= 800
  })
}
export const minDeliverPeriod = (duration, formData) => {
  return vuelidate.withParams({ type: 'minDeliverPeriod', duration }, () => {
    if (formData.isPrinted.length) { // 10 days or more for printed
      return moment(formData.deliverBy).diff(moment(new Date()), 'days') >= 9
    } else { // 3 days or more for digital
      return moment(formData.deliverBy).diff(moment(new Date()), 'days') >= 2
    }
  })
}
export const packetQuantity = (formData) => {
  return vuelidate.withParams({ type: 'packetQuantity' }, () => {
    return formData.packetQuantity <= formData.location.eligibleEmployees
  })
}
export const eventDateAlreadyTaken = (formData) => {
  return vuelidate.withParams({ type: 'eventDateAlreadyTaken' }, () => {
    return formData.locations.every((location) => {
      return location.eventDate !== formData.eventDate
    })
  })
}
export const locationNameAlreadyTaken = (localName, formData, f) => {
  return vuelidate.withParams({ type: 'locationNameAlreadyTaken' }, () => {
    return formData ? formData.every((location) => {
      return localName !== location.name || f.id === location.locationId
    }) : []
  })
}
export const minMaxValue = (localName, formData, min, max) => {
  return vuelidate.withParams({ type: 'minMaxValue' }, () => {
    if (Array.isArray(formData)) {
      return formData ? formData.every((location) => {
        return Number(location.eligibleEmployees) >= min && Number(location.eligibleEmployees) <= max
      }) : []
    } else {
      return Number(formData.eligibleEmployees) >= min && Number(formData.eligibleEmployees) <= max
    }
  })
}
export const exceedTotalEmp = (eligibleEmployees, min, max) => {
  return vuelidate.withParams({ type: 'exceedTotalEmp' }, () => {
    return Number(eligibleEmployees) <= max
  })
}
export const minValue = (eligibleEmployees, min) => {
  return vuelidate.withParams({ type: 'minValue' }, () => {
    return Number(eligibleEmployees) >= min
  })
}

/* Validation Rules
---------------------------------------------------------------- */
export const eventDetailValidations = (formData, selectedLocation, isInternalUser) => { // Digital, Printed, Rep
  const defaultFeilds = {
    contactEmail: { required, email, maxLength: maxLength(77) },
    contactPhone: { required, numeric },
    customerName: { required, maxLength: maxLength(50) },
    contactPhoneExt: { numeric, maxLength: maxLength(6) },
    contactEmail2: { email, maxLength: maxLength(77) },
    contactName2: { maxLength: maxLength(50) },
    contactEmail3: { email, maxLength: maxLength(77) },
    contactName3: { maxLength: maxLength(50) },
    contactEmail4: { email, maxLength: maxLength(77) },
    contactName4: { maxLength: maxLength(50) }
  }

  if (isInternalUser) {
    return {
      exchangeType: { required },
      ...defaultFeilds
    }
  } else {
    return defaultFeilds
  }
}

export const createNewEvent = (localName, formData, existingLocations, editLocation) => {
  const locationName = { required, maxLength: maxLength(50), locationNameAlreadyTaken: locationNameAlreadyTaken(localName, existingLocations) }
  return {
    locationName: { required, maxLength: maxLength(50), locationNameAlreadyTaken: locationNameAlreadyTaken(localName, existingLocations, formData) },
    addressLine1: { required, maxLength: maxLength(50) },
    addressLine2: { maxLength: maxLength(50) },
    city: { required, maxLength: maxLength(50) },
    state: { required },
    zipCode: { required, zipMaxLength: maxLength(9), zipMinLength: minLength(5), numeric },
    eligibleEmployees: { required, numeric, totalEmp: exceedTotalEmp(formData.eligibleEmployees, 0, formData.totalEmployees) },
    contactName: { required },
    contactPhone: { required, numeric, phoneMinLength: minLength(10), phoneMaxLength: maxLength(10) },
    contactPhoneExt: { numeric },
    contactEmail: { required, email },
    contactName2: { },
    contactEmail2: { email },
    contactName3: { },
    contactEmail3: { email },
    contactName4: { },
    contactEmail4: { email },
    multilingualIndicator: { required, alpha }
  }
}

/* Staffed Event vs Digital/Printed Event Validation Rules
---------------------------------------------------------------- */
export function validationBasedOnEventType(formData, selectedLocation, userType, isVirtualFeatureFlag) {
  const isInternalUser = isUserInternal(userType)
  const e = { ...eventDetailValidations(formData, selectedLocation, isInternalUser) }

  e.spanishMatNeededInd = { alpha, required }

  if (formData.location && formData.location.spanish) {
    e.spanishBenefitTranslation = { required }
    e.spanishQuantity = { numeric }
  }

  if (formData.isDigital.length > 0) {
    e.eventType = { required }
  }

  if (formData.isPrinted.length > 0 && formData.isRepNeeded.length === 0) { // Print only
    if (isInternalUser) {
      e.employeePackets = { required }

      if (formData.employeePackets) e.packetQuantity = { required, numeric, packetQuantity: packetQuantity(formData) }
      else e.packetQuantity = { numeric, packetQuantity: packetQuantity(formData) }
    }
  }

  if (formData.isPrinted.length > 0 || formData.isRepNeeded.length > 0) {
    e.shippingLine1 = { required, maxLength: maxLength(50) }
    e.shippingLine2 = { maxLength: maxLength(50) }
    e.shippingZipCode = { required, numeric, zipMaxLength: maxLength(9), zipMinLength: minLength(5) }
    e.shippingState = { required }
    e.shippingCity = { required }

    if (formData.spanishMatNeededInd) {
      e.spanishMatQty = { required }
    }

    // if (selectedLocation.multilingualIndicator === 'Y') {
    //   if (formData.spanishMatNeededInd) e.spanishMatQty = {
    //     numeric, required, totalEmpAtLocation: exceedTotalEmp(formData.spanishMatQty, 1, formData.eligibleEmployees),
    //     min: minValue(formData.spanishMatQty, 1)
    //   }
    //   else e.spanishMatQty = {
    //     numeric, totalEmpAtLocation: exceedTotalEmp(formData.spanishMatQty, 1,
    //       formData.eligibleEmployees), min: minValue(formData.spanishMatQty, 1)
    //   }
    // }
  }

  if (formData.isRepNeeded.length > 0) {
    // Staffed Event

    if (isInternalUser) {
      e.eyeMedAssociate = { required }
      e.presentationNeed = { required, alpha }

      if (formData.eyeMedAssociate) e.eyeMedAssociateName = { required }
    }

    e.startTime = { required, minTime: minTime(700), maxTime: maxTime(1800) }
    e.endTime = { required, minEndTime: minTime(800), maxTime: maxTime(1900), minEventDuration: minEventDuration(getEventDuration(formData)), maxEventDuration: maxEventDuration(getEventDuration(formData)) }
    e.raffledItem = { alpha, required }
    e.eventDate = { required, minDate, weekday, eventDateAlreadyTaken: eventDateAlreadyTaken(formData) }
    e.notes = { }

    if (isVirtualFeatureFlag || isVirtualFeatureFlag === '') {
      e.repType = { required }
      if (formData.repType === 'Virtual Representative') {
        e.supportType = { required }
        delete e.shipAddrSeparateInd
        delete e.shippingLine1
        delete e.shippingLine2
        delete e.shippingZipCode
        delete e.shippingState
        delete e.shippingCity
        delete e.raffledItem
        delete e.presentationNeed
      }
      if (formData.repType === 'Virtual Representative' && formData.supportType === 'Other') {
        e.supportDetails = { required }
      } else {
        delete e.supportDetails
      }
    }
  } else if (formData.isPrinted.length > 0) {
    e.deliverBy = { required, minPrintedPeriod: minDeliverPeriod(getDeliverPeriod(formData), formData) }
  } else {
    e.deliverBy = { required, minDigitalPeriod: minDeliverPeriod(getDeliverPeriod(formData), formData) }
  }

  return e
}
